import { useEffect, useState } from 'react';

const VERSION_JSON_PATH = '/version.json';

type VersionResponse = { version?: string };

const fetchServerVersion = async (): Promise<string | null> => {
  try {
    const res = await fetch(`${VERSION_JSON_PATH}?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as VersionResponse;
    return data.version ?? null;
  } catch {
    return null;
  }
};

/**
 * バンドルに埋め込まれた REACT_APP_BUILD_VERSION と、
 * サーバ側の /version.json を比較して新バージョン配信を検知するフック。
 *
 * - 初回マウント時と visibilitychange（タブ復帰時）にチェック
 * - REACT_APP_BUILD_VERSION が未設定、または /version.json が取得できない環境では何もしない
 * - 一度検知したら以降のチェックは行わない
 */
const useVersionCheck = (): boolean => {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState<boolean>(false);

  useEffect(() => {
    const currentVersion = process.env.REACT_APP_BUILD_VERSION ?? '';
    if (!currentVersion || isNewVersionAvailable) return undefined;

    let cancelled = false;

    const check = async (): Promise<void> => {
      const serverVersion = await fetchServerVersion();
      if (cancelled) return;
      if (serverVersion && serverVersion !== currentVersion) {
        setIsNewVersionAvailable(true);
      }
    };

    void check();

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isNewVersionAvailable]);

  return isNewVersionAvailable;
};

export default useVersionCheck;
